import { getUserCountry, isCountryInRegion } from '../theme-scripts.js';

class AnnouncementBar extends HTMLElement {
  constructor() {
    super();
    this.initializeMessages();
  }

  async initializeMessages() {
    try {
      // Landet for den butik/det marked siden er renderet for. Kommer fra Liquid,
      // saa den virker ogsaa paa dev-temaer og preview-URL'er.
      this.storeCountry = this.dataset.storeCountry || null;

      // Geo-opslaget maa ikke kunne blokere baren helt - fejler det, filtrerer vi
      // videre paa butikkens land, og ellers staar kun 'global' tilbage.
      const userCountry = await getUserCountry();

      this.filterMessagesByRegion(userCountry);
      this.initAnimation();
    } catch (error) {
      console.error('Error initializing announcement bar:', error);
    }
  }

  filterMessagesByRegion(userCountry) {
    this.querySelectorAll('.text-animation-slider__item').forEach((element) => {
      const region = element.dataset.countries;
      const shouldShow = region === 'global' || this.isMessageValidForRegion(region, userCountry);

      if (!shouldShow) {
        element.remove();
      }
    });
  }

  matchesRegion(region, country) {
    if (!country) return false;

    // Nogle valg i dropdownen er rene landekoder uden egen region-noegle
    // (CA, MX), saa fald tilbage til direkte sammenligning med landet.
    return isCountryInRegion(country, region) || region === country;
  }

  isMessageValidForRegion(region, userCountry) {
    const regions = window.storeSelectorConfig?.regions;

    switch (region) {
      case 'global':
        return true;
      case 'unsupported':
        if (!regions || !userCountry) return false;
        return !Object.values(regions).some((r) => r.countryCodes.includes(userCountry));
      default:
        // Vis beskeden hvis den passer til den besoegende ELLER til butikken siden
        // er renderet for - saa us.audocph.com viser US-beskeder til alle.
        return this.matchesRegion(region, userCountry) || this.matchesRegion(region, this.storeCountry);
    }
  }

  initAnimation() {
    this.animateContainer = this.querySelector('.text-animation-slider__container');
    if (!this.animateContainer) return;

    this.animateSlider = this.querySelector('.text-animation-slider__slider');
    this.animatedItems = this.animateSlider.querySelectorAll('.text-animation-slider__item');

    if (!this.animatedItems.length) return;

    if (this.animatedItems.length === 1) {
      const item = this.animatedItems[0];
      item.style.transition = 'none';
      item.style.transform = 'translate3d(0, 0, 0)';
      item.classList.add('active');
      return;
    }

    this.animatedItems[0].classList.add('active', 'animated');

    this.animateTextTimeout = null;
    this.animateTextTimeoutResize = null;
    this.firstLoad = true;
    this.paused = false;
    this.manualControl = false;
    this.autoRotate = this.dataset.autoRotate !== 'false';
    this.animateDuration = parseInt(this.dataset.speed || this.animateSlider.dataset.delay, 10) || 5000;

    if (this.animatedItems.length > 1) {
      this.addEventListener('mouseenter', () => this.pauseAnimation());
      this.addEventListener('mouseleave', () => this.resumeAnimation());
      this.addEventListener('focusin', () => this.pauseAnimation());
      this.addEventListener('focusout', () => this.resumeAnimation());
    }

    // Arrow navigation
    this.prevBtn = this.querySelector('[data-announcement-prev]');
    this.nextBtn = this.querySelector('[data-announcement-next]');

    if (this.animatedItems.length > 1) {
      // Show arrows when there are 2+ messages
      if (this.prevBtn) this.prevBtn.classList.remove('tw-hidden');
      if (this.nextBtn) this.nextBtn.classList.remove('tw-hidden');

      // Bind arrow clicks
      if (this.prevBtn) {
        this.prevBtn.addEventListener('click', () => {
          this.stopAutoRotate();
          this.goToPrev();
        });
      }
      if (this.nextBtn) {
        this.nextBtn.addEventListener('click', () => {
          this.stopAutoRotate();
          this.goToNext();
        });
      }

      // Start auto-rotate if enabled
      if (this.autoRotate) {
        this.animate();
      }
    }
  }

  goToNext() {
    const activeElement = this.animateSlider.querySelector('.active');
    if (!activeElement) return;

    const nextElement = activeElement.nextElementSibling || this.animateSlider.firstElementChild;
    this.transitionTo(activeElement, nextElement);
  }

  goToPrev() {
    const activeElement = this.animateSlider.querySelector('.active');
    if (!activeElement) return;

    const prevElement = activeElement.previousElementSibling || this.animateSlider.lastElementChild;
    this.transitionTo(activeElement, prevElement, true);
  }

  transitionTo(from, to, reverse = false) {
    const classes = {
      noAnimation: 'animated',
      animateIn: 'animate-in',
      animateOut: 'animate-out',
      active: 'active',
    };

    from.classList.add(classes.animateOut);
    from.classList.remove(classes.active, classes.noAnimation, classes.animateIn);

    to.classList.remove(classes.animateOut, classes.noAnimation);
    to.classList.add(classes.active, classes.animateIn);
  }

  clearTimer() {
    if (this.animateTextTimeout) {
      clearTimeout(this.animateTextTimeout);
      this.animateTextTimeout = null;
    }
  }

  stopAutoRotate() {
    // Brugeren har taget over via pilene - lad ikke hover/fokus starte den igen.
    this.manualControl = true;
    this.clearTimer();
  }

  pauseAnimation() {
    if (this.paused) return;
    this.paused = true;
    this.clearTimer();
  }

  resumeAnimation() {
    if (!this.paused) return;
    this.paused = false;

    if (this.autoRotate && !this.manualControl) {
      this.animate();
    }
  }

  animate() {
    // Ryd altid foerst, saa et ekstra kald ikke starter en parallel timer-kaede.
    this.clearTimer();

    this.animateTextTimeout = setTimeout(() => {
      this.animateTextTimeout = null;
      if (this.paused) return;
      this.goToNext();
      this.firstLoad = false;
      this.animate();
    }, this.animateDuration);
  }

  disconnectedCallback() {
    if (this.animateTextTimeout) {
      clearTimeout(this.animateTextTimeout);
    }
    if (this.animateTextTimeoutResize) {
      clearTimeout(this.animateTextTimeoutResize);
    }
  }
}

customElements.define('announcement-bar', AnnouncementBar);
